import type { Lesson } from '@yuan-math/curriculum';
import { insight, lesson, list, math, practice, steps, table, text, tip, warning } from './authoring.ts';

/**
 * Polynomials and factoring — the heart of Algebra I.
 *
 * The organising idea, and the one worth insisting on: factorising does not
 * make an expression *smaller* or *simpler* in any numerical sense. x² + 5x + 6
 * and (x + 2)(x + 3) agree at every value of x. What the product has that the
 * sum does not is a structure that makes the roots readable — and the whole of
 * quadratics is the payoff for noticing that.
 */
export const POLYNOMIAL_LESSONS: Lesson[] = [
  lesson({
    id: 'polynomials-01',
    unitId: 'unit-polynomials',
    conceptId: 'polynomials',
    title: 'Polynomials',
    summary: 'Adding, subtracting and multiplying expressions with powers — and why every term meets every term.',
    objectives: [
      'Add and subtract polynomials by collecting like terms',
      'Multiply two binomials, accounting for all four products',
      'Expand a squared bracket without losing the middle term',
    ],
    estimatedMinutes: 35,
    sections: {
      introduction: {
        title: 'Expressions with powers',
        blocks: [
          text('A **polynomial** is a sum of terms, each a number times a power of x:'),
          math('3x^{2} + 5x - 7'),
          text('You have handled these since Grade 6 under the name "expressions". What changes now is that you will multiply them together, and multiplying two polynomials is where most of Algebra I actually happens.'),
        ],
      },
      intuition: {
        title: 'Adding is easy; multiplying is where the care goes',
        blocks: [
          text('**Adding** is collecting like terms, and like terms means the *same power*. x² terms combine with x² terms and never with x terms, for the same reason you cannot add 3 metres to 3 square metres.'),
          math('(3x^{2} + 5x) + (2x^{2} - 8x) = 5x^{2} - 3x'),
          warning('Subtracting a bracket subtracts *everything* inside it. (3x² + 5x) − (2x² − 8x) is 3x² + 5x − 2x² + 8x — note that the −8x became +8x. Losing that sign is the commonest slip in the topic.', 'The subtracted bracket'),
          text('**Multiplying** rests on one rule: every term in the first bracket meets every term in the second.'),
          math('(x + 2)(x + 3) = x^{2} + 3x + 2x + 6 = x^{2} + 5x + 6'),
          insight('Four products, because two terms times two terms is four pairings. The two middle ones are both x terms, so they collect — which is where the 5x comes from.'),
          text('This matters most in the case that gets it wrong most often:'),
          math('(x + 3)^{2} = (x + 3)(x + 3) = x^{2} + 6x + 9'),
          warning('It is **not** x² + 9. A square means the bracket multiplied by itself, and that produces a middle term. Writing the square out as two brackets, every single time, removes the error entirely.', 'The missing middle term'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          text('The four products are literally four rectangles. Draw a rectangle (x + 2) wide and (x + 3) tall and split it:'),
          table(['', 'x', '+2'], [
            ['x', 'x²', '2x'],
            ['+3', '3x', '6'],
          ]),
          insight('Total area = x² + 2x + 3x + 6 = x² + 5x + 6. This is the area model from Grade 4 multiplication, unchanged — only the numbers have become letters.'),
          tip('The grid is worth drawing whenever a bracket has more than two terms. It makes it structurally impossible to miss a product, which a mental "FOIL" does not.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Expand and simplify (2x − 3)(x + 4) − (x − 1)².'),
          steps([
            { label: 'Expand the first product — all four pairings.', latex: '2x^{2} + 8x - 3x - 12 = 2x^{2} + 5x - 12' },
            { label: 'Expand the square as two brackets.', latex: '(x-1)(x-1) = x^{2} - 2x + 1' },
            { label: 'Subtract it — every term.', latex: '2x^{2} + 5x - 12 - x^{2} + 2x - 1' },
            { label: 'Collect like terms.', latex: 'x^{2} + 7x - 13' },
          ]),
          text('Two places to go wrong, and both appeared: the middle term of the square, and the signs when subtracting the bracket.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('polynomials', 4)] },
      independent: { title: 'On your own', blocks: [practice('polynomials', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('polynomials', 3, 'Squared brackets, where the middle term decides it.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Like terms have the same power of x, and only those combine.',
            'Subtracting a bracket subtracts every term inside it.',
            'Multiplying brackets pairs every term with every term.',
            '(x + a)² = x² + 2ax + a² — write it as two brackets and the middle term appears.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'factoring-01',
    unitId: 'unit-polynomials',
    conceptId: 'factoring',
    title: 'Factoring',
    summary: 'Running multiplication backwards, to recover the structure an expanded expression hides.',
    objectives: [
      'Take out the greatest common factor',
      'Factorise a quadratic into two brackets',
      'Recognise and factorise a difference of two squares',
    ],
    estimatedMinutes: 40,
    sections: {
      introduction: {
        title: 'Multiplication, backwards',
        blocks: [
          text('You can turn (x + 2)(x + 3) into x² + 5x + 6. **Factoring** is the same journey in reverse: starting from the sum and recovering the product.'),
          math('x^{2} + 5x + 6 = (x + 2)(x + 3)'),
          text('It is harder in this direction, in the same way that unmixing paint is harder than mixing it. It is also the single most useful skill in Algebra I, and it is worth being clear about why.'),
        ],
      },
      intuition: {
        title: 'Why bother, when the two are equal?',
        blocks: [
          text('Here is a question worth sitting with. Those two expressions are *equal* — they give the same value for every x. So what has been gained?'),
          insight('Structure. Ask "when is this zero?" of x² + 5x + 6 and there is nothing to grip. Ask it of (x + 2)(x + 3) and the answer falls out: a product is zero exactly when one of its factors is, so x = −2 or x = −3.'),
          text('Factoring does not simplify anything numerically. It exposes a shape that answers questions the other shape cannot. That is the whole reason the topic exists, and everything in the next three lessons depends on it.'),
          text('There are three patterns to know, and you try them in this order.'),
          list([
            '**Common factor first, always.** If every term shares something, take it out: 6x + 9 = 3(2x + 3). Doing this first often makes what remains much easier.',
            '**Difference of two squares.** a² − b² = (a + b)(a − b). Recognisable by having no middle term and a minus sign: x² − 16 = (x + 4)(x − 4).',
            '**Quadratic into two brackets.** For x² + bx + c, find two numbers that multiply to c and add to b.',
          ], true),
          warning('The signs in that last case follow from the two numbers, and are worth reasoning about rather than guessing. If c is positive, both numbers share a sign — the sign of b. If c is negative, they have opposite signs, and the larger takes the sign of b.', 'Getting the signs right'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          table(['Expression', 'Two numbers', 'Factorised'], [
            ['x² + 7x + 12', '3 and 4', '(x + 3)(x + 4)'],
            ['x² − 7x + 12', '−3 and −4', '(x − 3)(x − 4)'],
            ['x² + x − 12', '4 and −3', '(x + 4)(x − 3)'],
            ['x² − x − 12', '−4 and 3', '(x − 4)(x + 3)'],
            ['x² − 12', 'not a whole-number pair', 'does not factorise nicely'],
          ]),
          insight('The first four have the same numbers, 3 and 4, arranged with different signs — and every arrangement gives a different quadratic. That is why signs are not a detail here.'),
          tip('Always check by expanding. Factoring is one of the rare skills where verifying your answer is quicker than producing it, so there is no excuse for a wrong one.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Factorise 2x² − 18 fully.'),
          steps([
            { label: 'Common factor first — both terms are even.', latex: '2(x^{2} - 9)' },
            { label: 'What is left is a difference of two squares.', latex: 'x^{2} - 3^{2}' },
            { label: 'Apply the pattern.', latex: '2(x + 3)(x - 3)' },
            { label: 'Check by expanding.', latex: '2(x^{2} - 9) = 2x^{2} - 18' },
          ]),
          text('Skipping the first step would have left 2x² − 18 looking like nothing in particular. Taking the common factor out first is not merely tidy — it is what makes the pattern visible.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('factoring', 4)] },
      independent: { title: 'On your own', blocks: [practice('factoring', 6)] },
      challenge: { title: 'Something harder', blocks: [practice('factoring', 3, 'Negative signs, and knowing which pattern applies.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Factoring turns a sum into a product — the same value, a more useful shape.',
            'Take out a common factor before anything else.',
            'a² − b² = (a + b)(a − b), recognisable by having no middle term.',
            'For x² + bx + c, find two numbers multiplying to c and adding to b.',
            'Check by expanding. It takes seconds.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'radicals-01',
    unitId: 'unit-polynomials',
    conceptId: 'radicals',
    title: 'Simplifying Radicals',
    summary: 'Writing an irrational number in its plainest exact form, rather than rounding it away.',
    objectives: [
      'Simplify a radical by extracting perfect-square factors',
      'Add and subtract like radicals',
      'Explain why unlike radicals do not combine',
    ],
    estimatedMinutes: 30,
    sections: {
      introduction: {
        title: 'Exact, not approximate',
        blocks: [
          text('In Grade 8 you learned that √50 is irrational — its decimal never ends. Writing 7.07 instead is a rounding, and roundings accumulate.'),
          math('\\sqrt{50} = \\sqrt{25 \\times 2} = 5\\sqrt{2}'),
          text('That is the same number, written exactly. **Simplifying a radical** means pulling out every perfect square hiding inside it, so what remains under the root is as small as possible.'),
        ],
      },
      intuition: {
        title: 'What comes out, and what stays in',
        blocks: [
          text('The rule that makes this work is that a root splits across multiplication:'),
          math('\\sqrt{ab} = \\sqrt{a} \\times \\sqrt{b}'),
          insight('So find the largest perfect square dividing the number, take its root out front, and leave the rest inside. √72 = √(36 × 2) = 6√2.'),
          warning('A root does **not** split across addition. √(9 + 16) is 5, while √9 + √16 is 7. That was true in Grade 8 and it is still the single most important thing to remember about roots.', 'Multiplication only'),
          text('Once simplified, radicals add like terms in algebra — and for exactly the same reason.'),
          math('3\\sqrt{2} + 5\\sqrt{2} = 8\\sqrt{2}'),
          insight('Treat √2 as though it were x. Three of them plus five of them is eight of them. But 3√2 + 5√3 cannot be combined at all, any more than 3x + 5y can — they are different quantities.'),
          text('This is why simplifying first matters. √8 + √18 looks like two unlike radicals until you simplify: 2√2 + 3√2 = 5√2.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          table(['Radical', 'Largest square factor', 'Simplified'], [
            ['√12', '4', '2√3'],
            ['√45', '9', '3√5'],
            ['√72', '36', '6√2'],
            ['√98', '49', '7√2'],
            ['√30', 'none', '√30'],
          ]),
          tip('Take the *largest* square factor. √72 = √(4 × 18) = 2√18 is correct but unfinished, because 18 still contains a 9. Finishing gives 6√2.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Simplify √48 + √27 − √12.'),
          steps([
            { label: 'Simplify each radical.', latex: '\\sqrt{48} = 4\\sqrt{3}' },
            { label: 'And the next.', latex: '\\sqrt{27} = 3\\sqrt{3}' },
            { label: 'And the last.', latex: '\\sqrt{12} = 2\\sqrt{3}' },
            { label: 'Now they are all like radicals, so collect them.', latex: '4\\sqrt{3} + 3\\sqrt{3} - 2\\sqrt{3} = 5\\sqrt{3}' },
          ]),
          text('Nothing could be combined before simplifying, and everything could afterwards. That is the usual shape of these problems.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('radicals', 4)] },
      independent: { title: 'On your own', blocks: [practice('radicals', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('radicals', 3, 'Radicals that do not combine, however they are written.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'A root splits across multiplication, never across addition.',
            'Simplify by pulling out the largest perfect-square factor.',
            'Like radicals add like terms; unlike radicals do not combine at all.',
            'Simplify before deciding whether two radicals are alike.',
          ]),
        ],
      },
    },
  }),
];
